/* eslint-disable complexity */
import { type ReactNode } from 'react';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { Wysiwyg } from '@jsmdg/yoshi';
import { ContentType } from '../../../shared/enums/contentType';
import { type FragmentContext } from '../../../shared/types/fragmentContext';
import { type PageContent as PageContentType } from '../../../shared/types/pageContent';
import { type PageDocument } from '../../../shared/types/pageDocument';
import { TrackingCreativePrefix } from '../../enums/trackingCreativePrefix';
import { useCreatives } from '../../hooks/useCreatives';
import { HeroImage } from '../HeroImage';
import { NewsletterOptIn } from '../Newsletter/OptIn/NewsletterOptIn';
import { NewsletterSubscribe } from '../Newsletter/Subscribe/NewsletterSubscribe';
import { NewsletterUnsubscribe } from '../Newsletter/Unsubscribe/NewsletterUnsubscribe';
import { PageSliderWrapper } from '../PageSliderWrapper';
import styles from './Page.module.scss';

type PageContentProps = {
    readonly document?: PageDocument;
};

const PageContent = ({ document }: PageContentProps): JSX.Element | null => {
    const { navigationCreatives } = useCreatives();
    const { isMydays } = useFragmentContext<FragmentContext>();
    const hasNavigationCreative = navigationCreatives.length > 0;

    let verticalPosition = hasNavigationCreative ? 1 : 0;

    const renderPageContentItems = (content: PageContentType): ReactNode => {
        switch (content.type) {
            case ContentType.PageSlider:
                verticalPosition += 1;
                return (
                    <PageSliderWrapper
                        slides={content.data || []}
                        headline={content.title}
                        verticalPosition={verticalPosition}
                        hasNavigationCreative={hasNavigationCreative}
                        crossLinkPageTitle={content?.crossLinkPageTitle}
                        crossLinkPageRef={content?.crossLinkPageRef}
                        trackingPrefix={TrackingCreativePrefix.PageSlider}
                        trackingPath={document?.trackingPath}
                    />
                );

            case ContentType.Image:
                return (
                    <HeroImage
                        alt={content.alt || document?.headline || ''}
                        src={content.url}
                        srcset={content.srcset}
                        sizes={content.sizes}
                        width={823}
                        height={344}
                        lazyLoad={false}
                    />
                );

            case ContentType.Wysiwyg:
                return <Wysiwyg content={content.value} />;

            case ContentType.NewsletterOptIn:
                return (
                    <NewsletterOptIn
                        content={content.content}
                        predictedUserId={document?.newsletter?.predictedUserId || ''}
                        formHeading={content.formHeading}
                        formButtonLabel={content.formButtonLabel}
                        formAdditionalText={content.formAdditionalText}
                        formSuccessText={content.formSuccessText}
                    />
                );
            case ContentType.NewsletterSubscribeForm:
                return (
                    <NewsletterSubscribe
                        source={document?.newsletter?.source}
                        email={document?.newsletter?.email}
                        caption={content.caption}
                        emailPlaceholder={content.emailPlaceholder}
                        consentText={content.consentText}
                        buttonLabel={content.buttonLabel}
                        additionalText={content.additionalText}
                        successText={content.successText}
                        withNameFields={isMydays}
                    />
                );
            case ContentType.NewsletterUnsubscribeForm:
                return (
                    <NewsletterUnsubscribe
                        confirmationText={content.confirmationText}
                        formText={content.formText}
                        email={document?.newsletter?.email}
                        additionalData={document?.newsletter?.additionalData}
                    />
                );
            default:
                return <div>Unknown element</div>;
        }
    };

    if (!document?.contents) return null;

    return (
        <>
            {document?.contents.map((content, index) => {
                const key = `row-${index}`;

                return (
                    <div
                        key={key}
                        className={classNames('grid', {
                            'd-none d-sm-block': content.type === ContentType.Image,
                        })}
                    >
                        <div className={classNames('g-col-12', styles.row)}>
                            {renderPageContentItems(content)}
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export { PageContent };
